"use client";
import { SwitchLang } from "@/components/example/switch-lang";
import { Lang } from "@/i18n/settings";
import Logo from "../../shared/logo";

import { Shell } from "@/components/ui/shell";
import { useTranslation } from "@/i18n/client";
import { usePathname } from "next/navigation";

import Link from "@/components/shared/link";
import useScroll from "@/hooks/use-scroll";
import NAVBAR from "@/i18n/locales/en/navbar.json";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { MobileMenu } from "./mobile-menu";

type Props = {
  lang: Lang;
};

export default function NavBar({ lang }: Readonly<Props>) {
  const { t } = useTranslation(lang, "navbar");
  const pathname = usePathname();
  const [hoveringIndex, setHoveringIndex] = useState<number | null>(null);
  const [hoveredSubMenu, setHoveredSubMenu] = useState<number | null>(null);

  const scrolled = useScroll(50);

  function isActivePage(href?: string) {
    if (!href) {
      return;
    }

    const cleanHref = href.replace(/\/$/, "");

    return pathname === `/${lang}${cleanHref}`;
  }

  function isActiveSubPage(page: string, subMenu?: (typeof NAVBAR)["routes"]) {
    const pathn = pathname
      .replace(/^(\/[^\/]+\/[^\/]+).*$/, "$1")
      .replace(/\/$/, "");

    const d = subMenu?.find((x) => {
      const cleanHref = x?.href?.replace(/\/$/, "");
      return pathname === `/${lang}${cleanHref}`;
    });

    const cleanHref = d?.href?.replace(/\/$/, "");

    if (!cleanHref && pathn.endsWith("news")) {
      return "Media Center";
    }

    return pathn === `/${lang}${cleanHref}` ? page : "";
  }

  useEffect(() => {
    if (hoveringIndex) {
      setHoveringIndex(null);
      setHoveredSubMenu(null);
    }
  }, [pathname]);

  return (
    <div
      className={cn(
        "fixed z-[999] w-full",
        scrolled && "bg-background/20 backdrop-blur-xl",
      )}
    >
      <Shell className="overflow-visible py-6">
        <div className="flex items-center justify-between">
          <Link href="/">
            <Logo />
          </Link>
          <ul className="hidden gap-6 xl:flex xl:gap-10">
            {(
              t("routes", { returnObjects: true }) as (typeof NAVBAR)["routes"]
            ).map((menu, i) => {
              const hasSubMenus = menu.subMenus && menu.subMenus.length > 0;

              return (
                <li
                  key={menu.href || menu.title}
                  onMouseEnter={() => setHoveringIndex(i)}
                  onMouseLeave={() => {
                    setHoveringIndex(null);
                    setHoveredSubMenu(null);
                  }}
                  data-active={
                    hasSubMenus
                      ? isActiveSubPage(menu?.title, menu?.subMenus) ===
                        menu.title
                      : isActivePage(menu?.href)
                  }
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-2 uppercase transition-all duration-150 lg:text-lg ltr:font-playfair",
                    hoveringIndex !== null && i !== hoveringIndex
                      ? "opacity-50"
                      : "scale-105",
                  )}
                >
                  <div
                    className={cn(
                      "absolute -top-4 hidden h-2 w-2 rounded-full bg-primary",
                      hasSubMenus &&
                        isActiveSubPage(menu?.title, menu?.subMenus) ===
                          menu.title &&
                        "block",
                      isActivePage(menu.href ?? "#") && "block",
                    )}
                  />
                  {menu.href ? (
                    <Link href={menu.href} className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        {menu.title}
                        {hasSubMenus && <ChevronDown size={14} />}
                      </div>
                    </Link>
                  ) : (
                    <div
                      onTouchStart={() => {
                        if (hasSubMenus) {
                          setHoveringIndex(i);
                        }
                      }}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      {menu.title}
                      {hasSubMenus && <ChevronDown size={14} />}
                    </div>
                  )}

                  {hasSubMenus && (
                    <motion.ul
                      className="absolute top-full mt-2 flex flex-col gap-2 rounded-lg bg-foreground p-4 px-10 shadow-lg"
                      initial={{ opacity: 0, y: -10 }}
                      animate={
                        hoveringIndex === i
                          ? { opacity: 1, y: 0, display: "flex" }
                          : {
                              opacity: 0,
                              y: -10,
                              transitionEnd: { display: "none" },
                            }
                      }
                      transition={{ duration: 0.3 }}
                      onMouseEnter={() => setHoveringIndex(i)}
                      // onMouseLeave={() => setHoveringIndex(null)}
                    >
                      {menu.subMenus.map((subMenu, subIndex) => (
                        <motion.li
                          key={subMenu.href}
                          onMouseEnter={() => setHoveredSubMenu(subIndex)}
                          onMouseLeave={() => setHoveredSubMenu(null)}
                          initial={{ opacity: 1 }}
                          animate={{
                            opacity:
                              hoveredSubMenu !== null &&
                              hoveredSubMenu !== subIndex
                                ? 0.5
                                : 1,
                            scale: hoveredSubMenu === subIndex ? 1.05 : 1,
                          }}
                          className="whitespace-nowrap transition-all duration-150"
                        >
                          <Link href={subMenu.href} className="block">
                            {subMenu.title}
                          </Link>
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex gap-2">
            <SwitchLang />
            <MobileMenu lang={lang} />
          </div>
        </div>
      </Shell>
    </div>
  );
}
